import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getPendingLevelUp,
  submitLevelUp,
  getCharacterEnhancements,
  getLevelUpJobStatus,
  LevelUpOptions,
  EnhancementOption,
  CharacterEnhancement
} from '../api/levelup';
import { useOnboarding } from '../context/OnboardingContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import '../styles/global.css';

interface LocationState {
  characterId?: string;
}

const LevelUp: React.FC = () => {
  const { characterId: paramCharacterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldShowTooltip, dismissStep } = useOnboarding();
  const state = location.state as LocationState | null;

  const characterId = paramCharacterId || state?.characterId;
  const enhancementOptionsRef = useRef<HTMLDivElement>(null);

  const [levelUpData, setLevelUpData] = useState<LevelUpOptions | null>(null);
  const [currentEnhancements, setCurrentEnhancements] = useState<CharacterEnhancement[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newAvatarUrl, setNewAvatarUrl] = useState<string | null>(null);
  const [autoApplyOption, setAutoApplyOption] = useState<EnhancementOption | null>(null);

  const pollForAvatar = useCallback(async (jobId: string): Promise<string | null> => {
    for (let i = 0; i < 40; i++) { // 40 polls * 3s = 2min max
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const job = await getLevelUpJobStatus(jobId);
        if (job.status === 'completed' && job.result?.new_avatar_url) {
          return job.result.new_avatar_url;
        }
        if (job.status === 'failed') {
          console.warn('Avatar generation failed:', job.error);
          return null;
        }
      } catch {
        // Network error, keep trying
      }
    }
    return null; // Timed out
  }, []);

  const processLevelUp = useCallback(async (optionId?: string) => {
    if (!characterId) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitLevelUp(characterId, optionId);

      // Poll for avatar if async job was started
      let avatarUrl = result.avatar_url;
      if (result.job_id) {
        const newAvatar = await pollForAvatar(result.job_id);
        if (newAvatar) {
          avatarUrl = newAvatar;
        }
      }
      setNewAvatarUrl(avatarUrl);

      // Check if there's another pending level-up
      const [nextLevelUp, updatedEnhancements] = await Promise.all([
        getPendingLevelUp(characterId),
        getCharacterEnhancements(characterId).catch(() => [])
      ]);

      if (nextLevelUp.pending) {
        // Reset state for next level
        setLevelUpData(nextLevelUp);
        setCurrentEnhancements(updatedEnhancements);
        setSelectedOptionId(null);
        setNewAvatarUrl(null);
        setAutoApplyOption(null);

        // Handle auto-apply for next level
        if (nextLevelUp.auto_apply && nextLevelUp.options.length === 1) {
          setAutoApplyOption(nextLevelUp.options[0]);
        }
      } else {
        // Done with all level-ups
        setTimeout(() => navigate('/account'), 2000);
      }
    } catch (err) {
      setError('Failed to process level-up. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [characterId, navigate, pollForAvatar]);

  useEffect(() => {
    if (!characterId) {
      navigate('/account');
      return;
    }

    const fetchLevelUpData = async () => {
      try {
        const [levelUpRes, enhancementsRes] = await Promise.all([
          getPendingLevelUp(characterId),
          getCharacterEnhancements(characterId).catch(() => [])
        ]);

        if (!levelUpRes.pending) {
          navigate('/account');
          return;
        }

        setLevelUpData(levelUpRes);
        setCurrentEnhancements(enhancementsRes);

        // Handle auto-apply case
        if (levelUpRes.auto_apply && levelUpRes.options.length === 1) {
          setAutoApplyOption(levelUpRes.options[0]);
        }
      } catch (err) {
        setError('Failed to load level-up data');
      } finally {
        setLoading(false);
      }
    };

    fetchLevelUpData();
  }, [characterId, navigate]);

  // Auto-apply effect - triggers when auto_apply option is set
  useEffect(() => {
    if (autoApplyOption && !submitting && !newAvatarUrl && levelUpData?.auto_apply) {
      const timer = setTimeout(() => {
        processLevelUp(autoApplyOption.id);
      }, 1500); // Give user a moment to see what's being applied
      return () => clearTimeout(timer);
    }
  }, [autoApplyOption, submitting, newAvatarUrl, levelUpData, processLevelUp]);

  const handleOptionSelect = (optionId: string) => {
    setSelectedOptionId(optionId);
  };

  const getCurrentEnhancementForType = (typeId: string): CharacterEnhancement | undefined => {
    return currentEnhancements.find(e => e.type_id === typeId);
  };

  const handleConfirm = async () => {
    if (!characterId || selectedOptionId === null) return;
    processLevelUp(selectedOptionId);
  };

  const handleNoEnhancementConfirm = async () => {
    if (!characterId) return;
    processLevelUp();
  };

  if (loading) {
    return (
      <div className="page levelup-page">
        <div className="levelup-loading">Loading...</div>
      </div>
    );
  }

  if (error && !levelUpData) {
    return (
      <div className="page levelup-page">
        <div className="levelup-container">
          <div className="levelup-card">
            <div className="error">{error}</div>
            <button className="btn btn-primary" onClick={() => navigate('/account')} style={{ marginTop: 20 }}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!levelUpData) {
    return null;
  }

  const noEnhancements = levelUpData.options.length === 0;
  const isAutoApply = levelUpData.auto_apply && levelUpData.options.length === 1;

  return (
    <div className="page levelup-page">
      {submitting && (
        <div className="generating-overlay">
          <div className="spinner"></div>
          <p>Generating your new avatar...</p>
        </div>
      )}
      <div className="levelup-container">
        <div className="levelup-card">
          <div className="levelup-celebration">
            <div className="levelup-stars">&#11088;</div>
            <h1 className="levelup-title">Level Up!</h1>
            <div className="levelup-level">You reached Level {levelUpData.level}!</div>
          </div>

          {newAvatarUrl && (
            <div className="levelup-avatar-reveal">
              <div className="avatar-preview">
                <img src={newAvatarUrl} alt="New Avatar" />
              </div>
              <div className="avatar-reveal-text">Your character has evolved!</div>
            </div>
          )}

          {/* Enhancement options (with onboarding ref) */}
          <div ref={enhancementOptionsRef}>

          {/* Auto-apply state */}
          {!newAvatarUrl && isAutoApply && autoApplyOption && (
            <div className="levelup-auto-apply">
              <h2 className="slot-title">Applying Enhancement</h2>
              <div className="auto-apply-card" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <div className="option-name" style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '8px' }}>
                  {autoApplyOption.name}
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                  ({autoApplyOption.type_name})
                </div>
                {autoApplyOption.preview_description && (
                  <div className="option-description" style={{ marginTop: '12px', opacity: 0.85 }}>
                    {autoApplyOption.preview_description}
                  </div>
                )}
              </div>
              <div style={{ color: '#888', fontSize: '0.9rem' }}>
                Applying automatically...
              </div>
            </div>
          )}

          {/* No enhancements state */}
          {!newAvatarUrl && noEnhancements && (
            <div className="levelup-no-enhancements">
              <p style={{ marginBottom: '20px', color: '#666' }}>
                No enhancements available at this level. Your character will level up without changes.
              </p>
              <button
                className="btn btn-primary"
                onClick={handleNoEnhancementConfirm}
                disabled={submitting}
              >
                Continue
              </button>
            </div>
          )}

          {/* Multiple options selection */}
          {!newAvatarUrl && !noEnhancements && !isAutoApply && (
            <>
              <div className="levelup-slot">
                <h2 className="slot-title">
                  Choose your enhancement
                </h2>
                <div className="options-grid">
                  {levelUpData.options.map((option) => {
                    const currentEnhancement = getCurrentEnhancementForType(option.type_id);
                    return (
                      <button
                        key={option.id}
                        className={`option-card ${selectedOptionId === option.id ? 'selected' : ''}`}
                        onClick={() => handleOptionSelect(option.id)}
                        disabled={submitting}
                      >
                        <div className="option-name">{option.name}</div>
                        <div className="option-type" style={{ fontSize: '0.85rem', color: '#888', marginTop: '4px' }}>
                          ({option.type_name})
                        </div>
                        {currentEnhancement && (
                          <div className="replacing-info" style={{
                            marginTop: '8px',
                            padding: '4px 8px',
                            background: 'rgba(255, 193, 7, 0.2)',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: '#ffc107'
                          }}>
                            Replaces: {currentEnhancement.option_name}
                          </div>
                        )}
                        {option.preview_description && (
                          <div className="option-description">{option.preview_description}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div className="error-banner">
                  {error}
                  <button onClick={() => setError(null)} className="error-banner-close">&times;</button>
                </div>
              )}

              <button
                className="btn btn-primary levelup-confirm-btn"
                onClick={handleConfirm}
                disabled={selectedOptionId === null || submitting}
              >
                {submitting ? 'Upgrading...' : 'Confirm Choice'}
              </button>
            </>
          )}

          </div>{/* end enhancement options ref wrapper */}
        </div>
      </div>

      {/* Onboarding tooltip */}
      {shouldShowTooltip('levelUp', 1) && (
        <OnboardingTooltip
          targetRef={enhancementOptionsRef}
          title="Pick a power-up!"
          message="Choose how your character evolves. Each choice changes their look!"
          onDismiss={() => dismissStep('levelUp', 1)}
        />
      )}
    </div>
  );
};

export default LevelUp;
